//third-party
import { multiply } from "dinero.js";
import { LocalDate } from "@js-joda/core";
import { Button, Card, FormControl, InputGroup, Placeholder } from "react-bootstrap";
import React, { useRef } from "react";
import { Calendar3, InfoCircle } from "react-bootstrap-icons";

//spotsync
import { Money, showMoney, showMoneyShort } from "../Data/Money";
import { showBrowserDate, showDate } from "../Data/Date";
import { ListedPlan, SubscribePreviewResp, SubscribePreviewReq } from "../Data/ApiTypes";
import { AddSubtract } from "./AddSubtract";
import { useState, useEffect } from "react";
import { Api } from "../Data/Api";
import { MonthlyPriceBreakdown } from "./MonthlyPriceBreakdown";
import { toast } from "react-toastify";
import { isPreviewProrated } from "../Data/Common";
import { OptInFeesSelector, OptInFeesSelection } from "./OptInFeesSelector";
import "./MonthlyParkingDetails.scss";
import { DataEntitiesFinanceOperatorFeeScope } from "../shared/api/axios-client";

interface MonthlyParkingDetailsProps {
    color?:         string;
    facilityId:     number;
    start:          LocalDate;
    setStart?:      ( start: LocalDate ) => void;
    listedPlans:    ListedPlan[];
    selOpt:         ListedPlan;
    setSelOpt:      ( plan: ListedPlan ) => void;
    disabled?:      boolean;
    quantity?:      number;
    setQuantity?:   ( qty: number ) => void;
    /**Don't show fees or prorated pricing on preview, only on checkout details */
    isCheckoutPage: boolean; 
    isAch:          boolean;
    isPublicCheckout: boolean;
    onCouponPriceChange?: (priceId: number | null) => void;
    /**Callback when opt-in fees selection changes */
    onOptInFeesChange?: (selection: OptInFeesSelection) => void;
}

export function MonthlyParkingDetails( props: MonthlyParkingDetailsProps ) {
    const { 
        facilityId,
        start,
        setStart,
        listedPlans: monthlyOptions,
        selOpt,
        setSelOpt,
        quantity,
        setQuantity,
        disabled = false,
        isCheckoutPage,
        isPublicCheckout,
        isAch,
        onOptInFeesChange } = { ...props };

    const startEditable = setStart != null && !disabled;
    const dateInputRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [proratedFee, setProratedFee] = useState<Money>();
    const [previewData, setPreviewData] = useState<SubscribePreviewResp | null>(null);
    const [couponCode, setCouponCode] = useState("");
    const [optInFeesSelection, setOptInFeesSelection] = useState<OptInFeesSelection>({});
    const [appliedCouponPriceId, setAppliedCouponPriceId] = useState<number | null>(null);

    // Opt-in fees handlers
    const handleOptInFeesSelectionChange = (selection: OptInFeesSelection) => {
        setOptInFeesSelection(selection);
        onOptInFeesChange?.(selection);
    };

    useEffect(() => {
        const fetchPreview = async () => {
            setIsLoading(true);
            // Get selected opt-in fee IDs
            const selectedOptInFeeIds = Object.keys(optInFeesSelection)
                .filter(id => optInFeesSelection[parseInt(id)])
                .map(id => parseInt(id));

            const req: SubscribePreviewReq = {
                planEntryId: selOpt.planEntryId,
                start,
                quantity: quantity ?? 1,
                isAch,
                couponPriceId: appliedCouponPriceId,
                selectedOptInFees: selectedOptInFeeIds.length > 0 ? selectedOptInFeeIds : undefined
            };
            const result = await Api.subscriptionPreview(selOpt.planId, req);
            
            if (result.isOk) {
                setPreviewData(result.value);
                setProratedFee(result.value.price);
            }
            setIsLoading(false);
        };

        fetchPreview();
    }, [selOpt, quantity, appliedCouponPriceId, optInFeesSelection, start, isAch]);

    useEffect(() => {
    setCouponCode("");
    setAppliedCouponPriceId(null);
    props.onCouponPriceChange?.(null);
    }, [selOpt]);


    const monthlyRate = multiply(selOpt.price, quantity ?? 1);
    const displayRate = previewData ? previewData.fullUnitPrice : monthlyRate;
    const spotCount = quantity ?? 1;
    const spotLabel = `${spotCount} ${spotCount === 1 ? "spot" : "spots"} · billed monthly`;

    async function handleApplyCoupon() { 
        if (!couponCode.trim()) return;

        try {
            const res = await Api.applyCouponToPlan(selOpt.planId, couponCode);

            if (res?.isOk && res.value) {
                setAppliedCouponPriceId(res.value.priceId);
                props.onCouponPriceChange?.(res.value.priceId);
                toast.success("Price applied successfully");
            } else {
                setCouponCode("");
                setAppliedCouponPriceId(null);
                props.onCouponPriceChange?.(null)
                toast.error("Sorry, the price code was not found");
            }
        } catch (err) {
            setCouponCode("");
            props.onCouponPriceChange?.(null)
            setAppliedCouponPriceId(null);
            toast.error("Sorry, the price code was not found");
        }
    }

    function openDatePicker() {
        const el = dateInputRef.current;
        if (!el || disabled) return;
        try {
            el.showPicker?.();
        } catch {
            el.focus();
            el.click();
        }
    }

    return <Card className='border-2 border-primary my-2 shadow'>
        <Card.Header className='text-center header-gradient'>
            Monthly Parking Options
        </Card.Header>
        <Card.Body>
            {monthlyOptions.map( listedPlan => {
                const selected = listedPlan === selOpt;
                return (
                    <div
                        key={listedPlan.planEntryId}
                        className={`plan-option${selected ? ' plan-option--selected' : ''}${disabled ? ' plan-option--disabled' : ''}`}
                        onClick={() => !disabled && setSelOpt( listedPlan )}
                    >
                        <div className="plan-option__radio" />
                        <span className="plan-option__name">{listedPlan.name}</span>
                        <span className="plan-option__price">{showMoneyShort( listedPlan.price )}</span>
                    </div>
                );
            })}
                {isCheckoutPage && isPublicCheckout && (
                <div className="mt-3 mb-2">
                    <InputGroup>
                    <FormControl
                        placeholder="Enter price code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        disabled={appliedCouponPriceId != null || disabled} 
                    />
                    {appliedCouponPriceId == null ? (
                        <Button
                        onClick={handleApplyCoupon}
                        variant="primary"
                        >
                        Apply
                        </Button>
                    ) : (
                        <Button
                        onClick={() => {
                            setCouponCode("");
                            setAppliedCouponPriceId(null);
                            props.onCouponPriceChange?.(null); 
                            toast.info("Price code removed");
                        }}
                        variant="secondary"
                        >
                        Reset
                        </Button>
                    )}
                    </InputGroup>
                </div>
                )}

                {appliedCouponPriceId != null && (
                <span className="badge bg-success fs-6">
                    Price Applied
                </span>
                )}

            <div className="mpd-summary mt-3">
                {(quantity !== undefined && setQuantity !== undefined) && (
                    <div className="mpd-summary__row">
                        <span className="mpd-summary__label">Spots</span>
                        <AddSubtract
                            quantity={quantity}
                            set={qty => disabled !== true && setQuantity(qty)}
                            disabled={disabled}
                        />
                    </div>
                )}

                <div className="mpd-summary__row mpd-summary__row--date">
                    <div className="mpd-summary__date-top">
                        <span className="mpd-summary__label">Start date</span>
                        {setStart != null ? (
                            <div
                                className={`mpd-date-pill${startEditable ? "" : " mpd-date-pill--disabled"}`}
                                onClick={openDatePicker}
                                role={startEditable ? "button" : undefined}
                                tabIndex={startEditable ? 0 : undefined}
                                onKeyDown={e => {
                                    if (!startEditable) return;
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openDatePicker();
                                    }
                                }}
                                aria-label="Choose start date"
                            >
                                <Calendar3 className="mpd-date-pill__icon" size={16} />
                                <span className="mpd-date-pill__value">{showDate(start)}</span>
                                <input
                                    ref={dateInputRef}
                                    type="date"
                                    className="mpd-date-pill__input"
                                    min={showBrowserDate(LocalDate.now())}
                                    value={showBrowserDate(start)}
                                    disabled={!startEditable}
                                    onChange={e => {
                                        if (!e.target.value || !setStart) return;
                                        setStart(LocalDate.parse(e.target.value));
                                    }}
                                    tabIndex={-1}
                                    aria-hidden
                                />
                            </div>
                        ) : (
                            <span className="mpd-date-pill mpd-date-pill--static">
                                <Calendar3 className="mpd-date-pill__icon" size={16} />
                                <span className="mpd-date-pill__value">{showDate(start)}</span>
                            </span>
                        )}
                    </div>
                    {setStart != null && (
                        <div className="mpd-summary__hint">
                            <InfoCircle size={12} className="flex-shrink-0" />
                            <span>The first day you need to park</span>
                        </div>
                    )}
                </div>

                <div className="mpd-summary__rate">
                    <div>
                        <div className="mpd-summary__rate-label">Monthly rate</div>
                        <div className="mpd-summary__rate-sub">{spotLabel}</div>
                    </div>
                    <div className="mpd-summary__rate-price">
                        <span className="mpd-summary__rate-amount">{showMoney(displayRate)}</span>
                        <span className="mpd-summary__rate-unit"> / mo</span>
                    </div>
                </div>
            </div>

            {isCheckoutPage && (
                <div className="mt-3">
                    <OptInFeesSelector
                        disabled={disabled}
                        facilityId={facilityId}
                        feeScope={DataEntitiesFinanceOperatorFeeScope.NUMBER_1} // 1 for monthly parking
                        feePaymentType={isAch ? 1 : 0} // ACH vs Card payment type
                        onSelectionChange={handleOptInFeesSelectionChange}
                        isCheckout={isCheckoutPage}
                    />
                </div>
            )}

            {isCheckoutPage && (
                <div className="mt-3 w-100">
                    {isLoading ? (
                        <div className="border rounded p-2">
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={4} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={5} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                            </div>
                        </div>
                    ) : previewData && (
                        <div className="fade-in">
                            <MonthlyPriceBreakdown 
                                price={previewData.price}
                                fees={previewData.fees}
                                showTotalLabel="Due Today"
                                showProratedHint={isPreviewProrated(previewData)}
                            />
                        </div>
                    )}
                </div>
            )}
        </Card.Body>
    </Card>;
}
